import axios from "axios";

import { ROUTES } from "@/constants/routes";
import { getLoginNextPath } from "@/utils/general";
import { authMetrics } from "@/utils/newrelicHelpers";
import { shouldSkipAuthRedirect as createShouldSkipAuthRedirect } from "@/utils/urlValidation";

export const getAxiosErrorMessage = (err) => {
  if (axios.isAxiosError && axios.isAxiosError(err)) {
    const data = err?.response?.data;
    if (typeof data === "string") return data;
    if (typeof data?.detail === "string") return data.detail;
    if (data && typeof data === "object") {
      const firstError = Object.values(data).flat()[0];
      if (typeof firstError === "string") return firstError;
    }
    return err.message;
  }
  return "Something went wrong";
};

// Explicit REACT_APP_BACKEND_URL wins (e2e CI build points at the local backend);
// else production derives the Render URL and dev falls back to localhost.
const BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production"
    ? `https://${process.env.REACT_APP_RENDER_API_SERVICE}.onrender.com`
    : "http://localhost:8000");

const allowed401s = [
  "/api/token/",
  "/api/token/refresh/",
  "/api/token/logout/",
  ROUTES.PROVIDER_TEST_API, // Magic link endpoints should not trigger auth redirects
  ROUTES.PROVIDER_REGISTER_API, // Provider registration API should not trigger auth redirects
  ROUTES.REGISTER_API, // User registration API should not trigger auth redirects
]; // most 401s should take user to /login/. add valid 401s here

const shouldSkipAuthRedirect = (url) =>
  createShouldSkipAuthRedirect(url, BASE_URL, allowed401s);

// Circuit breaker to prevent infinite request loops
const MAX_REQUESTS = 50;
const REQUEST_WINDOW_MS = 10000;
let requestCounter = 0;
let requestWindowStart = 0;

// A fixed window, not a debounce: resetting only after 10s of quiet let a member
// paging through an intake at a normal pace hit the cap and get logged out.
const incrementRequestCounter = () => {
  const now = Date.now();
  if (now - requestWindowStart >= REQUEST_WINDOW_MS) {
    requestWindowStart = now;
    requestCounter = 0;
  }
  requestCounter++;

  if (requestCounter > MAX_REQUESTS) {
    console.error(
      "EMERGENCY STOP: Request limit exceeded. Clearing auth state."
    );
    authMetrics.logCircuitBreaker(requestCounter);
    localStorage.clear();
    window.location = "/login/";
    throw new Error("Request rate limit exceeded");
  }
};

// Token refresh queue to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers.forEach((cb) => cb(null));
  refreshSubscribers = [];
};

// set interceptors to pass auth token if we have it in the local storage
axios.interceptors.request.use(
  (config) => {
    incrementRequestCounter();
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// if response is 401 and it's not a retry, refresh access token
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Skip if already retried or if this is an allowed 401
    if (
      originalRequest._retry ||
      !error.response ||
      error.response.status !== 401
    ) {
      return Promise.reject(error);
    }

    if (shouldSkipAuthRedirect(originalRequest.url)) {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      authMetrics.logRefreshQueued(
        originalRequest.url,
        refreshSubscribers.length + 1
      );

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token) => {
          if (token) {
            originalRequest.headers.authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          } else {
            reject(error);
          }
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use fetch() to avoid interceptor recursion
      const refreshToken = localStorage.getItem("refreshToken");
      const response = await fetch(`${BASE_URL}/api/token/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Refresh failed");
      }

      const data = await response.json();
      const newAccessToken = data.access;

      localStorage.setItem("accessToken", newAccessToken);
      authMetrics.logRefreshSuccess(
        originalRequest.url,
        refreshSubscribers.length
      );

      // Dispatch custom event to notify React components about token change
      // NOTE: This cross-boundary communication pattern is used because:
      // 1. This interceptor is plain JS, outside React's component tree
      // 2. Multiple React components may need to respond to token changes
      // 3. Alternative (injecting React state setters) would tightly couple axios to React
      // The Authentication component listens for this event and updates its state accordingly
      window.dispatchEvent(
        new CustomEvent("tokenRefreshed", {
          detail: { accessToken: newAccessToken },
        })
      );

      // Update authorization header for retried request
      originalRequest.headers.authorization = `Bearer ${newAccessToken}`;

      // Notify all queued requests
      onRefreshed(newAccessToken);

      isRefreshing = false;

      // Retry the original request
      return axios(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshFailed();

      authMetrics.logRefreshFailed(
        originalRequest.url,
        refreshSubscribers.length,
        refreshError.message
      );

      // Clear tokens locally without making API call to avoid cascade
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("currentUser");

      // If already on login page, don't redirect (prevents circular redirect to /login/?next=/login/)
      const currentPath = window.location.pathname;
      if (currentPath.startsWith("/login")) {
        return Promise.reject(refreshError);
      }

      // Redirect to login
      const searchParams = new URLSearchParams(window.location.search);
      window.location = getLoginNextPath(
        window.location.pathname,
        searchParams
      );

      return Promise.reject(refreshError);
    }
  }
);

const getRequest = async (url, successCallback, errorCallback) => {
  const queryParams = new URLSearchParams(window.location.search);
  const impersonate = queryParams.get("__impersonate");
  if (impersonate) {
    if (url.includes("?")) {
      url += `&__impersonate=${impersonate}`;
    } else {
      url += `?__impersonate=${impersonate}`;
    }
  }
  return axios
    .get(BASE_URL + url)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      // Let the interceptor handle 401s to avoid duplicate handling
      console.error(error);
      if (errorCallback) {
        return errorCallback(error, error.response && error.response.data);
      }
      throw error;
    });
};

const postRequest = (
  url,
  data,
  successCallback,
  errorCallback,
  requestConfig
) => {
  const fullUrl = BASE_URL + url;
  return axios
    .post(fullUrl, data, requestConfig)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      // Let the interceptor handle 401s to avoid duplicate handling
      console.error(error);
      if (errorCallback) {
        return errorCallback(error, error.response && error.response.data);
      }
      throw error;
    });
};

const patchRequest = (url, data, successCallback, errorCallback) => {
  return axios
    .patch(BASE_URL + url, data)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      // Let the interceptor handle 401s to avoid duplicate handling
      console.error(error);
      if (errorCallback) {
        return errorCallback(error, error.response && error.response.data);
      }
      throw error;
    });
};

const putRequest = (url, data, successCallback, errorCallback) => {
  return axios
    .put(BASE_URL + url, data)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      // Let the interceptor handle 401s to avoid duplicate handling
      console.error(error);
      if (errorCallback) {
        return errorCallback(error, error.response && error.response.data);
      }
      throw error;
    });
};

const deleteRequest = (url, successCallback, errorCallback) => {
  return axios
    .delete(BASE_URL + url)
    .then((response) => {
      if (successCallback) {
        return successCallback(response);
      }
    })
    .catch((error) => {
      // Let the interceptor handle 401s to avoid duplicate handling
      console.error(error);
      if (errorCallback) {
        return errorCallback(error, error.response && error.response.data);
      }
      throw error;
    });
};

export {
  BASE_URL,
  deleteRequest,
  getRequest,
  postRequest,
  patchRequest,
  putRequest,
};
